import { useCallback, useEffect, useRef, useState } from 'react'
import { Runde, istUnterbrechung } from './scheduler/scheduler'
import { heute as heuteIso } from './scheduler/datum'
import type { Aufgabe, Rueckmeldung, Rundenergebnis } from './scheduler/typen'
import type { Format } from './config'
import { LokalerSpeicher, neueId } from './speicher/lokal'
import { besterMoment, neuSicher, anzahlSicher, fortschrittssatz } from './speicher/fortschritt'
import type { GespeicherteAntwort, Profil } from './speicher/typen'
import type { Kartenstand } from './scheduler/typen'
import { AUFGABEN_PRO_RUNDE, BLITZ_MS } from './config'
import { tonRichtig, tonKette, tonFertig, tonSchalten, tonAufwecken, tonBlitz } from './ton'
import { rundeMelden as raumMelden, type Tagesstand } from './raum/api'

export type Uebungszustand = 'laedt' | 'frage' | 'rueckmeldung' | 'fertig'

/** Wie lange ein Treffer gefeiert wird, bevor die naechste Aufgabe kommt. */
const JUBEL_MS = 340

const speicher = new LokalerSpeicher()

/**
 * Der Klebstoff zwischen Scheduler, Speicher und Oberflaeche.
 *
 * Hier wird entschieden, wann die naechste Aufgabe kommt und wie lange eine
 * Rueckmeldung stehen bleibt. Die Regel: Ein Treffer haelt 340 ms auf, ein
 * Fehler wartet auf das Kind. Zwanzig Aufgaben mit je zwei Sekunden Jubel
 * waeren vierzig Sekunden Warten in einer Fuenf-Minuten-Runde, und genau
 * daran sterben Lern-Apps: Sie werden nicht langweilig, sie werden zaeh.
 */
export function useUebung(
  spiegel?: {
    aufgabeZeigen: (aufgabe: Aufgabe) => void
    rueckmeldungZeigen: (rueck: Rueckmeldung, aufgabe: Aufgabe) => void
    fertigZeigen: (ergebnis: Rundenergebnis) => void
  },
  optionen?: { format?: Format },
) {
  const [zustand, setZustand] = useState<Uebungszustand>('laedt')
  const [aufgabe, setAufgabe] = useState<Aufgabe | null>(null)
  const [rueck, setRueck] = useState<Rueckmeldung | null>(null)
  const [ergebnis, setErgebnis] = useState<Rundenergebnis | null>(null)
  const [profil, setProfil] = useState<Profil | null>(null)
  const [blitz, setBlitz] = useState<'gut' | 'kette' | 'schnell' | null>(null)
  /** Was der Freundinnen-Raum zur gerade beendeten Runde meldet, falls es einen gibt. */
  const [raumstand, setRaumstand] = useState<Tagesstand | null>(null)
  /**
   * Zaehler, der die Stoppuhr der Spielformate zuruecksetzt.
   *
   * Wird hochgezaehlt, wenn die App aus dem Hintergrund zurueckkommt oder eine
   * Antwort wegen zu langer Pause verworfen wurde. Die Formate messen selbst,
   * weil nur sie wissen, wann die erste Eingabe kam; das Signal zum Neustarten
   * gehoert aber hierher.
   */
  const [stoppuhr, setStoppuhr] = useState(0)
  /** Zeigt kurz an, dass eine Unterbrechung erkannt wurde. */
  const [pause, setPause] = useState(false)
  /**
   * Ein wahrer Satz ueber den eigenen Fortschritt, oder null.
   *
   * Wird aus der Antwort-Historie abgeleitet und erscheint auf der
   * Abschlussseite. Gibt es nichts Belegbares zu sagen, bleibt er leer.
   */
  const [fortschritt, setFortschritt] = useState<string | null>(null)
  /** Wie viele Karten insgesamt sicher sitzen. Steuert die Ballon-Farbwelten. */
  const [sicher, setSicher] = useState(0)

  const runde = useRef<Runde | null>(null)
  const gepuffert = useRef<GespeicherteAntwort[]>([])
  const uhr = useRef<number | null>(null)
  /**
   * Sperre gegen doppelt gemeldete Antworten.
   *
   * Der Zustand allein reicht dafuer nicht: Zwei Beruehrungen im selben
   * Ereignisdurchlauf sehen beide noch "frage", und die Aufgabe wuerde zweimal
   * gewertet. Ein Ref ist sofort gesetzt.
   */
  const antwortLaeuft = useRef(false)
  /** Die Kartenstaende von vor der Runde, fuer den Vorher-Nachher-Vergleich. */
  const staendeVorher = useRef<Kartenstand[]>([])
  const spiegelRef = useRef(spiegel)
  spiegelRef.current = spiegel
  // Als Ref, damit `starten` stabil bleibt und die Runde nicht bei jedem
  // Neuzeichnen neu beginnt.
  const formatRef = useRef(optionen?.format)

  // Den Zeitgeber immer aufraeumen. Ohne das feuert ein ausstehender
  // Weiter-Sprung noch, nachdem die Seite gewechselt wurde.
  useEffect(() => {
    return () => {
      if (uhr.current !== null) window.clearTimeout(uhr.current)
    }
  }, [])

  /**
   * Die App war im Hintergrund und ist zurueck.
   *
   * Das passiert im Alltag staendig: Nachricht kommt rein, jemand ruft, das
   * Tablet wird kurz weggelegt. Die Stoppuhr laeuft dabei weiter, und ohne
   * diesen Griff wuerde die naechste Antwort als quaelend langsam gewertet,
   * obwohl das Kind sie sofort wusste. Also wird die Messung neu gestartet,
   * sobald die Seite wieder sichtbar ist.
   */
  useEffect(() => {
    function sichtbarkeit() {
      if (document.visibilityState === 'visible') setStoppuhr((n) => n + 1)
    }
    document.addEventListener('visibilitychange', sichtbarkeit)
    window.addEventListener('focus', sichtbarkeit)
    return () => {
      document.removeEventListener('visibilitychange', sichtbarkeit)
      window.removeEventListener('focus', sichtbarkeit)
    }
  }, [])

  const starten = useCallback(async () => {
    tonAufwecken()
    setZustand('laedt')
    setErgebnis(null)
    setRueck(null)
    setRaumstand(null)
    setFortschritt(null)
    setPause(false)
    setStoppuhr((n) => n + 1)
    antwortLaeuft.current = false
    gepuffert.current = []

    const heute = heuteIso()
    const [p, staende] = await Promise.all([
      speicher.profilLaden(),
      speicher.staendeLaden(heute),
    ])
    setProfil(p)
    tonSchalten(p.tonAn)

    staendeVorher.current = staende.map((x) => ({ ...x }))
    setSicher(anzahlSicher(staende))

    const neu = new Runde(staende, heute, Math.random, formatRef.current)
    runde.current = neu

    const erste = neu.naechsteAufgabe()
    setAufgabe(erste)
    setZustand(erste ? 'frage' : 'fertig')
    if (erste) spiegelRef.current?.aufgabeZeigen(erste)
  }, [])

  const abschliessen = useCallback(async () => {
    const r = runde.current
    if (!r) return
    const e = r.ergebnis()

    const heute = heuteIso()
    const geschafft = e.anzahlAufgaben >= AUFGABEN_PRO_RUNDE

    // Speichern darf die Abschlussseite nie verhindern. Scheitert es, ist der
    // Lernstand dieser einen Runde verloren, aber das Kind hat trotzdem
    // zwanzig Aufgaben gerechnet und bekommt dafuer sein Ergebnis zu sehen.
    try {
      await speicher.antwortenAnhaengen(gepuffert.current)
      await speicher.rundeSpeichern({
        profilId: profil?.id ?? 'unbekannt',
        datum: heute,
        anzahlAufgaben: e.anzahlAufgaben,
        geschafft,
        schnellsteMs: e.schnellsteMs,
        laengsteKette: e.laengsteKette,
      })
    } catch (fehler) {
      console.error('[zahlenflug] Runde konnte nicht gespeichert werden:', fehler)
    }

    // Den Fortschritts-Satz aus der vollstaendigen Historie ableiten, die
    // gerade gespeicherten Antworten eingeschlossen.
    try {
      const historie = await speicher.antwortenLaden()
      const moment = besterMoment(historie, heute)
      const dazu = neuSicher(staendeVorher.current, e.staende)
      setFortschritt(fortschrittssatz(moment, dazu))
      setSicher(anzahlSicher(await speicher.staendeLaden(heute)))
    } catch {
      setFortschritt(null)
    }

    setErgebnis(e)
    setZustand('fertig')
    tonFertig()
    spiegelRef.current?.fertigZeigen(e)

    // Dem Raum Bescheid geben, falls Lina in einem ist. Bewusst erst NACH
    // dem lokalen Speichern und ohne await davor: Ist der Server langsam oder
    // gar nicht da, sieht sie trotzdem sofort ihre Abschlussseite. Der
    // Trainer bleibt ohne Netz vollständig benutzbar.
    void raumMelden(e.anzahlAufgaben, geschafft).then(setRaumstand)
  }, [profil])

  const weiter = useCallback(() => {
    if (uhr.current !== null) {
      window.clearTimeout(uhr.current)
      uhr.current = null
    }
    setRueck(null)
    setBlitz(null)
    setPause(false)
    antwortLaeuft.current = false

    const r = runde.current
    if (!r) return

    const naechste = r.naechsteAufgabe()
    if (!naechste) {
      void abschliessen()
      return
    }
    setAufgabe(naechste)
    setZustand('frage')
    spiegelRef.current?.aufgabeZeigen(naechste)
  }, [abschliessen])

  const antworten = useCallback(
    (eingabe: number, zeitMs: number) => {
      const r = runde.current
      const aktuelle = aufgabe
      if (!r || !aktuelle || zustand !== 'frage') return
      if (antwortLaeuft.current) return
      antwortLaeuft.current = true

      // Zu lange her, um eine Denkzeit zu sein: Das Kind wurde unterbrochen.
      // Diese Antwort wird gar nicht gewertet, sonst lernt der Scheduler eine
      // Zahl, die nie gemessen wurde, und stuft eine gekonnte Karte zurueck.
      if (istUnterbrechung(zeitMs)) {
        antwortLaeuft.current = false
        setPause(true)
        setStoppuhr((n) => n + 1)
        window.setTimeout(() => setPause(false), 1600)
        return
      }

      const antwort = r.antwortMelden({ eingabe, zeitMs })
      setRueck(antwort)
      setZustand('rueckmeldung')
      spiegelRef.current?.rueckmeldungZeigen(antwort, aktuelle)

      gepuffert.current.push({
        id: neueId(),
        profilId: profil?.id ?? 'unbekannt',
        karte: aktuelle.variante.karte,
        variante: aktuelle.variante.id,
        format: aktuelle.format,
        richtig: antwort.richtig,
        zeitMs,
        zone: antwort.zone,
        erstelltAm: new Date().toISOString(),
        hochgeladen: false,
      })

      // Die Kartenstaende gleich wegschreiben, das Antwortprotokoll erst am
      // Rundenende. Der Stand ist das Wertvolle und umfasst nur 55 Zeilen;
      // die Historie waechst dagegen und darf nicht bei jeder Aufgabe komplett
      // neu geschrieben werden.
      void speicher.staendeSpeichern(r.ergebnis().staende)

      if (antwort.zone === 'hakt') {
        // Der Merksatz bleibt stehen, bis das Kind weitertippt. Hier ist
        // Lesezeit sinnvoll investiert, anders als beim Jubeln.
        return
      }

      const blitzschnell = antwort.zone === 'gewusst' && zeitMs < BLITZ_MS

      tonRichtig(antwort.kette)
      if (antwort.ketteGezuendet) {
        tonKette()
        setBlitz('kette')
      } else if (blitzschnell) {
        tonBlitz()
        setBlitz('schnell')
      } else {
        setBlitz('gut')
      }

      uhr.current = window.setTimeout(() => {
        uhr.current = null
        weiter()
      }, JUBEL_MS)
    },
    [aufgabe, zustand, profil, weiter],
  )

  useEffect(() => {
    void starten()
  }, [starten])

  return {
    zustand,
    aufgabe,
    rueck,
    ergebnis,
    profil,
    blitz,
    raumstand,
    stoppuhr,
    pause,
    fortschritt,
    sicher,
    antworten,
    weiter,
    neuStarten: starten,
  }
}

export { speicher }
